import { useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import dayjs, { type Dayjs } from 'dayjs';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import ProductionCostTables from '@/components/Approval/@components/ProductionCost';
import { getProductionCostDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildProductionCostSubject,
  PRODUCTION_COST_LOAD_FAILED_MESSAGE,
  productionCostDataFromDraftSource,
  type ProductionCostData,
  type ProductionCostDraftSource,
} from '@/utils/approval/productionCost';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type ProductionCostFieldProps = {
  /** 신규는 양식 응답, 편집은 저장값 복원이 끝난 뒤 true — 편집은 그때 저장 마감 월로 1회 조회 */
  ready: boolean;
};

/**
 * SAP 거절(424)은 SAP 안내 문구 그대로(레거시 동일) — 데이터가 없을 때도 SAP 가 거절로 줌.
 * 서버 문구 「…실패하였습니다: SAP Exception: {SAP 문구}」 에서 SAP 문구만. 그 밖은 공통 안내
 */
const loadFailedMessage = (error: unknown): string | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 424) return null;
  const message = error.response.data?.message;
  if (typeof message !== 'string') return null;
  const sapMessage = message.split('SAP Exception:').pop()?.trim();
  return sapMessage || null;
};

/**
 * 생산비 마감(회계)(216) — 마감 월을 고르면 SAP 생산비 집계를 조회해 표로, 제목도 덮어씀.
 * 선택 범위는 올해 1~12월, 다른 해로 저장된 문서는 그 달까지 넓힘.
 * 편집은 저장 마감 월로 다시 조회하고 차이내역 내용만 복원, 제목은 저장값 유지(레거시 동일)
 */
const ProductionCostField = ({ ready }: ProductionCostFieldProps) => {
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'productionCost' });
  const requestIdRef = useRef(0);
  const startedRef = useRef(false);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
    },
    [],
  );

  const fetchSettlement = useCallback(
    async (closingMonth: string) => {
      const requestId = ++requestIdRef.current;
      const current = getValues('productionCost');
      if (!current) return;
      setValue('productionCost', { ...current, loading: true, data: null });
      const finish = (data: ProductionCostData | null) => {
        const latest = getValues('productionCost');
        if (latest) {
          setValue('productionCost', { ...latest, loading: false, data });
        }
      };
      try {
        const res = await getNestedDataFetcher<ProductionCostDraftSource>(
          getProductionCostDraftSourceUrl(closingMonth),
        );
        if (requestId !== requestIdRef.current) return;
        finish(
          productionCostDataFromDraftSource(
            res,
            closingMonth,
            current.savedGroupwareDocumentNumber,
          ),
        );
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        finish(null);
        snackbar.showError({
          message:
            loadFailedMessage(error) ?? t(PRODUCTION_COST_LOAD_FAILED_MESSAGE),
        });
      }
    },
    [getValues, setValue, snackbar, t],
  );

  useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    const saved = getValues('productionCost')?.closingMonth;
    if (saved) fetchSettlement(saved);
  }, [ready, getValues, fetchSettlement]);

  const { minDate, maxDate } = useMemo(() => {
    const now = dayjs();
    let min = now.startOf('year');
    let max = now.endOf('year').startOf('month');
    const saved = state?.closingMonth
      ? dayjs(`${state.closingMonth}-01`)
      : null;
    if (saved?.isValid()) {
      if (saved.isBefore(min)) min = saved;
      if (saved.isAfter(max)) max = saved;
    }
    return { minDate: min, maxDate: max };
  }, [state?.closingMonth]);

  const handleMonthChange = (value: Dayjs | null) => {
    if (!value?.isValid()) return;
    if (value.isBefore(minDate, 'month') || value.isAfter(maxDate, 'month'))
      return;
    const closingMonth = value.format('YYYY-MM');
    const current = getValues('productionCost');
    if (!current || current.closingMonth === closingMonth) return;
    setValue(
      'productionCost',
      { ...current, closingMonth, notes: {}, data: null },
      { shouldDirty: true },
    );
    setValue('subject', buildProductionCostSubject(closingMonth, t), {
      shouldDirty: true,
    });
    fetchSettlement(closingMonth);
  };

  // 조회 실패 뒤 같은 달을 다시 고르면 내용·제목은 그대로 두고 재조회
  const handleMonthReselect = (value: Dayjs | null) => {
    const current = getValues('productionCost');
    if (!value?.isValid() || !current || current.loading || current.data)
      return;
    const closingMonth = value.format('YYYY-MM');
    if (current.closingMonth === closingMonth) fetchSettlement(closingMonth);
  };

  const handleNoteChange = (code: string, note: string) => {
    const current = getValues('productionCost');
    if (!current) return;
    setValue(
      'productionCost',
      { ...current, notes: { ...current.notes, [code]: note } },
      { shouldDirty: true },
    );
  };

  if (!state) return null;

  return (
    <FieldBlock className="production-cost-field">
      <ProductionCostTables
        closingMonth={state.closingMonth}
        monthSlot={
          <DatePicker
            value={
              state.closingMonth ? dayjs(`${state.closingMonth}-01`) : null
            }
            onChange={handleMonthChange}
            onMonthChange={handleMonthReselect}
            views={['year', 'month']}
            openTo="month"
            format="YYYY-MM"
            placeholder="YYYY-MM"
            minDate={minDate}
            maxDate={maxDate}
            sx={{ width: 160 }}
          />
        }
        notice={
          state.loading ? (
            <ApprovalNoticeBox role="status">
              {t('데이터를 불러오는 중입니다...')}
            </ApprovalNoticeBox>
          ) : null
        }
        settlement={state.data?.api_data ?? null}
        notes={state.notes}
        onNoteChange={handleNoteChange}
        t={t}
        lang={i18n.language}
      />
    </FieldBlock>
  );
};

export default ProductionCostField;
